"use client";

import React from "react";
import { cn } from "@/lib/utils";

interface AvatarProps {
  name: string;
  imageUrl?: string;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
  variant?: "circle" | "rounded";
}

const Avatar: React.FC<AvatarProps> = ({
  name,
  imageUrl,
  className,
  size = "md",
  variant = "circle",
}) => {
  // Generate initials from name
  const getInitials = (fullName: string): string => {
    if (!fullName || fullName.trim() === "") return "?";

    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  };

  // Generate background color based on name
  const getBackgroundColor = (fullName: string): string => {
    if (!fullName || fullName.trim() === "") return "bg-gray-500";

    const colors = [
      "bg-red-500",
      "bg-blue-500",
      "bg-green-500",
      "bg-yellow-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
      "bg-orange-500",
      "bg-cyan-500",
      "bg-emerald-500",
      "bg-rose-500",
    ];

    // Create a simple hash from the name
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use absolute value to ensure positive index
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };

  // Size configurations
  const sizeClasses = {
    sm: {
      container: "w-8 h-8 min-w-8 min-h-8",
      text: "text-xs",
    },
    md: {
      container: "w-10 h-10 min-w-10 min-h-10",
      text: "text-sm",
    },
    lg: {
      container: "w-12 h-12 min-w-12 min-h-12",
      text: "text-base",
    },
    xl: {
      container: "w-16 h-16 min-w-16 min-h-16",
      text: "text-lg",
    },
  };

  // Variant classes
  const variantClasses = {
    circle: "rounded-full",
    rounded: "rounded-lg",
  };

  const initials = getInitials(name);
  const backgroundColor = getBackgroundColor(name);
  const sizeConfig = sizeClasses[size];
  const variantClass = variantClasses[variant];

  return (
    <div
      className={cn(
        "relative flex items-center justify-center font-medium text-white select-none shrink-0 overflow-hidden",
        sizeConfig.container,
        variantClass,
        !imageUrl && backgroundColor,
        className,
      )}
      title={name}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Hide the image and show initials on error
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      ) : null}
      {!imageUrl && (
        <span className={cn("font-semibold", sizeConfig.text)}>{initials}</span>
      )}
      {/* Fallback initials for broken images */}
      {imageUrl && (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center font-semibold",
            backgroundColor,
            sizeConfig.text,
            "opacity-0",
          )}
          style={{
            display: "none",
          }}
          onError={() => {
            // This will be shown if the image fails to load
          }}
        >
          {initials}
        </span>
      )}
    </div>
  );
};

// Legacy components for backwards compatibility
interface LegacyAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const LegacyAvatar = React.forwardRef<HTMLDivElement, LegacyAvatarProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
        className,
      )}
      {...props}
    />
  ),
);
LegacyAvatar.displayName = "LegacyAvatar";

export { Avatar, LegacyAvatar };
export type { AvatarProps };
